import { defineStore } from 'pinia'
import { DeepReadonly, readonly, ref } from 'vue'

import { TrackingService } from '@/client/services'
import { useSystemStore } from '@/client/store/useSystemStore'
import { API } from '@/client/utils/api'
import { TCampaign } from '@/typings/api/campaigns.type'
import { APIError } from '@/typings/api/error'
import { TApplicant, TEntryCount, TGetApplicantsParams } from '@/typings/api/twitter_applicants.type'
import { TPrize } from '@/typings/api/twitter_campaign_prizes.type'

type DrawerTab = '' | 'profile' | 'entryHistory' | 'winHistory' | 'streamHistory'

type UpdateApplicantOptions = {
  applicantId: number
  isWinner?: boolean
  wasWinner?: boolean
  isResponder?: boolean
  isCandidate?: boolean
  isNg?: boolean
}

type APIGetTwitterApplicantDrawerResponse = {
  id: number
  account_id: string
  screen_name: string
  name: string
  picture_url: string
  is_positive: boolean
  is_warning: boolean
  is_ng: boolean
  ng_applicant_id: number | null
  is_follower: boolean
  follows_count: number
  followers_count: number
  join_datetime: string | null
  description: string | null
  is_winner: boolean
  is_candidate: boolean
  is_responder: boolean
  is_suspended_account: boolean
  positive_keywords: string[]
  negative_keywords: string[]
  posts: {
    id: number
    publish_datetime: string
  }[]
  entry_histories: {
    datetime: string | null
    campaign_name: string
  }[]
  win_histories: {
    datetime: string | null
    campaign_name: string
  }[]
  stream_histores: {
    id: number
    status: string
    body: string
    action_datetime: string
    send_type: string
  }[]
}

type GetTwitterApplicantDrawerResponse = {
  data?: APIGetTwitterApplicantDrawerResponse
  error?: APIError
}

export type TwitterApplicantState = {
  firstLoaded: boolean
  error: boolean
  total: number
  applicants: TApplicant[]
  params: TGetApplicantsParams
  campaign: TCampaign | null
  campaignPrize: TPrize | null
  showAdvanceFilter: boolean
  isApplicantDrawer: boolean
  applicantDrawerLoading: boolean
  drawerApplicantId: number | null
  applicantDrawerTab: DrawerTab
  apiApplicantDrawer: APIGetTwitterApplicantDrawerResponse | null
  isLoading: boolean
  entryCounts: number
  isDmSending: boolean
}

const createInitialState = (): TwitterApplicantState => ({
  firstLoaded: false,
  error: false,
  campaign: null,
  campaignPrize: null,
  applicants: [],
  total: 0,
  params: {
    campaignId: 0,
    prizeId: 0,
    sortBy: '-publishDatetime',
    pagingNo: 1,
    limitCount: 25,
    isNewFollower: '',
    postType: '',
    isMedia: '',
    isFollowing: '',
    isWinner: '',
    isWebInstantwinWinner: '',
    wasWinner: -1,
    isResponder: '',
    isCandidate: '',
    isWarning: '',
    isPositive: '',
    followersCount: ['', ''],
    followsRate: ['', ''],
    tweetsCount: ['', ''],
    totalCampaignEntryCount: ['', ''],
    totalCampaignWinningCount: ['', ''],
    postsCount: ['', ''],
    accountName: '',
    isJoinDatetime: '',
    isProfileImage: '',
    isDescription: '',
    isWidget: -1,
    forCsv: 0,
    hunterPoint: null,
    entryDateFrom: '',
    entryDateTo: '',
    isSuspendedAccount: ''
  },
  showAdvanceFilter: false,
  isApplicantDrawer: false,
  applicantDrawerLoading: false,
  drawerApplicantId: null,
  applicantDrawerTab: '',
  apiApplicantDrawer: null,
  isLoading: false,
  entryCounts: 0,
  isDmSending: false
})

export const useTwitterApplicantStore = defineStore('twitterApplicantStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const firstLoaded = ref<TwitterApplicantState['firstLoaded']>(initialState.firstLoaded)
  const error = ref<TwitterApplicantState['error']>(initialState.error)
  const campaign = ref<TwitterApplicantState['campaign']>(initialState.campaign)
  const campaignPrize = ref<TwitterApplicantState['campaignPrize']>(initialState.campaignPrize)
  const applicants = ref<TwitterApplicantState['applicants']>(initialState.applicants)
  const total = ref<TwitterApplicantState['total']>(initialState.total)
  const params = ref<TwitterApplicantState['params']>(initialState.params)
  const showAdvanceFilter = ref<TwitterApplicantState['showAdvanceFilter']>(initialState.showAdvanceFilter)
  const isApplicantDrawer = ref<TwitterApplicantState['isApplicantDrawer']>(initialState.isApplicantDrawer)
  const applicantDrawerLoading = ref<TwitterApplicantState['applicantDrawerLoading']>(
    initialState.applicantDrawerLoading
  )
  const drawerApplicantId = ref<TwitterApplicantState['drawerApplicantId']>(initialState.drawerApplicantId)
  const applicantDrawerTab = ref<TwitterApplicantState['applicantDrawerTab']>(initialState.applicantDrawerTab)
  const apiApplicantDrawer = ref<TwitterApplicantState['apiApplicantDrawer']>(initialState.apiApplicantDrawer)
  const isLoading = ref<TwitterApplicantState['isLoading']>(initialState.isLoading)
  const entryCounts = ref<TwitterApplicantState['entryCounts']>(initialState.entryCounts)
  const isDmSending = ref<TwitterApplicantState['isDmSending']>(initialState.isDmSending)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    firstLoaded.value = nextState.firstLoaded
    error.value = nextState.error
    campaign.value = nextState.campaign
    campaignPrize.value = nextState.campaignPrize
    applicants.value = nextState.applicants
    total.value = nextState.total
    params.value = nextState.params
    showAdvanceFilter.value = nextState.showAdvanceFilter
    isApplicantDrawer.value = nextState.isApplicantDrawer
    applicantDrawerLoading.value = nextState.applicantDrawerLoading
    drawerApplicantId.value = nextState.drawerApplicantId
    applicantDrawerTab.value = nextState.applicantDrawerTab
    apiApplicantDrawer.value = nextState.apiApplicantDrawer
    isLoading.value = nextState.isLoading
    entryCounts.value = nextState.entryCounts
    isDmSending.value = nextState.isDmSending
  }

  const fetchApplicants = async (): Promise<void> => {
    isLoading.value = true
    error.value = false

    await systemStore.setLoading(true)
    const result = await API.get('/twitter_applicants', { params: params.value })
    await systemStore.setLoading(false)

    firstLoaded.value = true
    isLoading.value = false

    if (!result.data) {
      error.value = true
      return
    }

    applicants.value = result.data.applicants
    if (params.value.pagingNo === 1 && result.data.dataCount !== undefined) {
      total.value = result.data.dataCount
    }

    if (result.data.isDmSending !== undefined) {
      isDmSending.value = result.data.isDmSending
    }
  }

  const fetchEntryCount = async (payload: DeepReadonly<TEntryCount>): Promise<void> => {
    const result = await API.get('/twitter_applicants/entry_counts', { params: payload })
    if (!result.data) {
      error.value = true
      return
    }
    entryCounts.value = result.data.count
    if (result.data.isDmSending !== undefined) {
      isDmSending.value = result.data.isDmSending
    }
  }

  const fetchCampaign = async (): Promise<void> => {
    const result = await API.get(`/campaigns/${params.value.campaignId}`)
    if (result.data) {
      if (!result.data.account) {
        result.data.account = {
          id: null,
          name: '削除されたアカウント',
          pictureUrl: ''
        }
      }
      campaign.value = result.data
      if (result.data.isDmSending !== undefined) {
        isDmSending.value = result.data.isDmSending
      }
    }
  }

  const fetchCampaignPrize = async (): Promise<void> => {
    const result = await API.get(`/twitter_campaign_prizes/${params.value.prizeId}`)
    if (result.data) {
      campaignPrize.value = result.data
    }
  }

  const setParams = (newParams: Partial<TGetApplicantsParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  const toggleShowAdvanceFilter = async (): Promise<void> => {
    TrackingService.sendEvent('click:応募一覧（X）|詳細検索を終了')
    showAdvanceFilter.value = !showAdvanceFilter.value
  }

  const fetchApplicantDrawer = async (): Promise<void> => {
    if (!drawerApplicantId.value) return

    applicantDrawerLoading.value = true

    const response: GetTwitterApplicantDrawerResponse = await API.get(
      `v1/twitter_applicant_drawers/${drawerApplicantId.value}`
    )

    applicantDrawerLoading.value = false

    if (response.data) {
      apiApplicantDrawer.value = response.data
    }
  }

  const openApplicantDrawer = async (payload: { applicantId: number; tab: string }): Promise<void> => {
    isApplicantDrawer.value = true
    drawerApplicantId.value = payload.applicantId
    applicantDrawerTab.value = payload.tab as DrawerTab
    await fetchApplicantDrawer()
  }

  const closeApplicantDrawer = async (): Promise<void> => {
    isApplicantDrawer.value = false
    applicantDrawerLoading.value = false
    drawerApplicantId.value = null
    applicantDrawerTab.value = ''
    apiApplicantDrawer.value = null
  }

  const changeApplicantDrawerTab = async (payload: DrawerTab): Promise<void> => {
    applicantDrawerTab.value = payload
  }

  const updateApplicant = async (payload: UpdateApplicantOptions) => {
    if (payload.isNg === true && campaign.value && apiApplicantDrawer.value) {
      const response = await API.post('/twitter_ng_applicants', {
        groupId: campaign.value.groupId,
        screenNames: [apiApplicantDrawer.value.screen_name]
      })

      if (response.data) {
        await fetchApplicants()
        await fetchCampaignPrize()
        await fetchApplicantDrawer()
      }

      return response
    }

    if (payload.isNg === false && apiApplicantDrawer.value) {
      const response = await API.delete(`/twitter_ng_applicants/${apiApplicantDrawer.value?.ng_applicant_id}`)

      if (response.data) {
        await fetchApplicants()
        await fetchCampaignPrize()
        await fetchApplicantDrawer()
      }

      return response
    }

    const response = await API.put(`/twitter_applicants/${payload.applicantId}`, {
      prizeId: campaignPrize.value?.id,
      isWinner: payload.isWinner,
      wasWinner: payload.wasWinner,
      isResponder: payload.isResponder,
      isCandidate: payload.isCandidate
    })

    if (response.data) {
      await fetchApplicants()
      await fetchCampaignPrize()
      await fetchApplicantDrawer()
    }

    return response
  }

  const updateNgAccount = async (payload: { screenName: string; isNg: boolean; ngApplicantId: number | null }) => {
    if (!campaign.value) return null
    if (!payload.isNg && !payload.ngApplicantId) return null

    const response = payload.isNg
      ? await API.post('/twitter_ng_applicants', {
          groupId: campaign.value.groupId,
          screenNames: [payload.screenName]
        })
      : await API.delete(`/twitter_ng_applicants/${payload.ngApplicantId}`)

    if (response.data) await fetchApplicants()
    return response
  }

  const prevApplicantDrawer = async (): Promise<void> => {
    switch (applicantDrawerTab.value) {
      case 'profile':
        TrackingService.sendEvent('click:ユーザー詳細|プロフィール|ページネーション|前へ')
        break
      case 'entryHistory':
        TrackingService.sendEvent('click:ユーザー詳細|参加履歴|ページネーション|前へ')
        break
      case 'winHistory':
        TrackingService.sendEvent('click:ユーザー詳細|当選履歴|ページネーション|前へ')
        break
      case 'streamHistory':
        TrackingService.sendEvent('click:ユーザー詳細|配信履歴|ページネーション|前へ')
        break
      default:
        break
    }

    applicantDrawerLoading.value = true

    if (!applicants.value || !drawerApplicantId.value) {
      applicantDrawerLoading.value = false
      return
    }

    const applicant_ids = applicants.value.map(v => v.id)
    const index = applicant_ids.indexOf(drawerApplicantId.value)

    if (index === 0 && params.value.pagingNo === 1) {
      applicantDrawerLoading.value = false
      return
    }

    if (index === 0 && params.value.pagingNo > 1) {
      const prev_page_no = params.value.pagingNo - 1

      setParams({ pagingNo: prev_page_no })
      await fetchApplicants()

      const prev_applicant_id = applicants.value[params.value.limitCount - 1]
        ? applicants.value[params.value.limitCount - 1].id
        : null

      if (!prev_applicant_id) {
        applicantDrawerLoading.value = false
        return
      }

      drawerApplicantId.value = prev_applicant_id
      await fetchApplicantDrawer()
      return
    }

    const prev_applicant_id = applicants.value[index - 1] ? applicants.value[index - 1].id : null

    if (!prev_applicant_id) {
      applicantDrawerLoading.value = false
      return
    }

    drawerApplicantId.value = prev_applicant_id
    await fetchApplicantDrawer()
  }

  const nextApplicantDrawer = async (): Promise<void> => {
    switch (applicantDrawerTab.value) {
      case 'profile':
        TrackingService.sendEvent('click:ユーザー詳細|プロフィール|ページネーション|次へ')
        break
      case 'entryHistory':
        TrackingService.sendEvent('click:ユーザー詳細|参加履歴|ページネーション|次へ')
        break
      case 'winHistory':
        TrackingService.sendEvent('click:ユーザー詳細|当選履歴|ページネーション|次へ')
        break
      case 'streamHistory':
        TrackingService.sendEvent('click:ユーザー詳細|配信履歴|ページネーション|次へ')
        break
      default:
        break
    }

    applicantDrawerLoading.value = true

    if (!applicants.value || !drawerApplicantId.value) {
      applicantDrawerLoading.value = false
      return
    }

    const applicant_ids = applicants.value.map(v => v.id)
    const index = applicant_ids.indexOf(drawerApplicantId.value)

    if (index === applicant_ids.length - 1 && params.value.pagingNo * params.value.limitCount >= total.value) {
      applicantDrawerLoading.value = false
      return
    }

    if (index === applicant_ids.length - 1 && params.value.pagingNo * params.value.limitCount < total.value) {
      const next_page_no = params.value.pagingNo + 1

      setParams({ pagingNo: next_page_no })
      await fetchApplicants()

      const next_applicant_id = applicants.value[0] ? applicants.value[0].id : null

      if (!next_applicant_id) {
        applicantDrawerLoading.value = false
        return
      }

      drawerApplicantId.value = next_applicant_id
      await fetchApplicantDrawer()
      return
    }

    const next_applicant_id = applicants.value[index + 1] ? applicants.value[index + 1].id : null

    if (!next_applicant_id) {
      applicantDrawerLoading.value = false
      return
    }

    drawerApplicantId.value = next_applicant_id
    await fetchApplicantDrawer()
  }

  return {
    firstLoaded: readonly(firstLoaded),
    error: readonly(error),
    campaign: readonly(campaign),
    campaignPrize: readonly(campaignPrize),
    applicants: readonly(applicants),
    total: readonly(total),
    params: readonly(params),
    showAdvanceFilter: readonly(showAdvanceFilter),
    isApplicantDrawer: readonly(isApplicantDrawer),
    applicantDrawerLoading: readonly(applicantDrawerLoading),
    drawerApplicantId: readonly(drawerApplicantId),
    applicantDrawerTab: readonly(applicantDrawerTab),
    apiApplicantDrawer: readonly(apiApplicantDrawer),
    isLoading: readonly(isLoading),
    entryCounts: readonly(entryCounts),
    isDmSending: readonly(isDmSending),
    reset,
    fetchApplicants,
    fetchEntryCount,
    fetchCampaign,
    fetchCampaignPrize,
    setParams,
    toggleShowAdvanceFilter,
    fetchApplicantDrawer,
    openApplicantDrawer,
    closeApplicantDrawer,
    changeApplicantDrawerTab,
    updateApplicant,
    updateNgAccount,
    prevApplicantDrawer,
    nextApplicantDrawer
  }
})
